import React, { useState, useMemo } from 'react'
import { MagnifyingGlass } from '@phosphor-icons/react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Icon,
  Input,
  Tabs,
  TabsList,
  TabsTrigger
} from '@shopflo/ui'
import { useDynamicSegment } from 'src/lib/contexts/DynamicSegmentContext'
import { CategoryTabConfigType, CategoryTabType, DynamicSegmentFieldType, DynamicSegmentFilterType } from 'src/lib/types/segment'
import { useLocale } from 'src/lib/hooks/useLocal'
import { DynamicSegmentColumnCategoryTabs } from 'src/assets/SegmentsConfig'
import { getDynamicSegmentIcon, getDynamicSegmentUIFilterType, isEventMetricCountField, mapEventData } from 'src/lib/utils/segmentsHelper'
import { generateUUID } from 'src/lib/utils/helpers'

interface FilterModalProps {
  /** Trigger component to open the dropdown */
  trigger: React.ReactNode
  /** Callback when a field is selected */
  onSelectField: (filer: DynamicSegmentFilterType) => void
}

type FilterModalUserHover =
  | { field: DynamicSegmentFieldType; eventName: string | null }
  | 'cleared'
  | null

interface FilterModalFieldPanelProps {
  groupedFields: Record<string, DynamicSegmentFieldType[]>
  defaultHover: { field: DynamicSegmentFieldType; eventName: string | null } | null
  t: ReturnType<typeof useLocale>['t']
  onFieldChosen: (options: { field: DynamicSegmentFieldType; value?: string[] }) => void
}

/** Left list + right detail. Remounted when defaultHoverKey changes so hover state resets without useEffect. */
const FilterModalFieldPanel: React.FC<FilterModalFieldPanelProps> = ({
  groupedFields,
  defaultHover,
  t,
  onFieldChosen
}) => {
  /** null = use defaultHover; object = pointer hover; 'cleared' = mouse-leave (empty detail until remount or re-hover) */
  const [userHover, setUserHover] = useState<FilterModalUserHover>(null)

  let hoveredField: DynamicSegmentFieldType | null = null
  let hoveredEventName: string | null = null

  if (userHover === 'cleared') {
    // leave both null
  } else if (userHover) {
    hoveredField = userHover.field
    hoveredEventName = userHover.eventName
  } else if (defaultHover) {
    hoveredField = defaultHover.field
    hoveredEventName = defaultHover.eventName
  }

  const handleSelectField = (field: DynamicSegmentFieldType, value?: string[]) => {
    onFieldChosen({ field, value })
  }

  return (
    <>
      <div className='w-[257px] border-r border-zinc-100 overflow-y-auto'>
        {DynamicSegmentColumnCategoryTabs.map((category) => {
          if (!groupedFields[category.key]) {
            return null
          }
          return (
            <div key={category.key} className='mb-4'>
              <div className='px-4 py-2'>
                <p className='text-text-xs-medium text-zinc-500'>
                  {category.label}
                </p>
              </div>

              {groupedFields[category.key].map((field: DynamicSegmentFieldType) => {
                if (category.key === 'EVENT_ATTRIBUTES') {
                  if (field.columnReference === 'EVENT_NAME' && field.allowedValues && field.allowedValues.length > 0) {
                    return field.allowedValues.map((value: string) => (
                      <div
                        key={`${field.id}:${value}`}
                        className={`flex items-center gap-2 px-2 py-1.5 mx-1.5 rounded-md cursor-pointer transition-colors 
                          ${hoveredField?.id === field.id && hoveredEventName === value
                            ? 'bg-zinc-50'
                            : 'hover:bg-zinc-50'
                          }`}
                        onClick={() => handleSelectField(field, [value])}
                        onMouseEnter={() => { setUserHover({ field, eventName: value }) }}
                        onMouseLeave={() => { setUserHover('cleared') }}
                      >
                        <Icon IconComponent={getDynamicSegmentIcon(category.key)} size='sm' className='text-zinc-400' />
                        <span className='text-text-sm-regular truncate text-zinc-600'>
                          {mapEventData(value, field.metadata).name}
                        </span>
                      </div>
                    ))
                  }
                  return null
                }
                return (
                  <div
                    key={field.id}
                    className={`flex items-center gap-2 px-2 py-1.5 mx-1.5 rounded-md cursor-pointer transition-colors ${hoveredField?.id === field.id
                      ? 'bg-zinc-50'
                      : 'hover:bg-zinc-50'
                      }`}
                    onClick={() => handleSelectField(field)}
                    onMouseEnter={() => setUserHover({ field, eventName: null })}
                    onMouseLeave={() => setUserHover('cleared')}
                  >
                    <Icon IconComponent={getDynamicSegmentIcon(category.key)} size='sm' className='text-zinc-400' />
                    <span className='text-text-sm-regular truncate text-zinc-600'>
                      {field.name}
                    </span>
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>

      <div className='flex-1 p-5 bg-white overflow-y-auto'>
        {hoveredField ? (
          <div className='flex flex-col gap-2'>
            <div className='flex flex-col gap-1'>
              {(() => {
                const IconComponent = getDynamicSegmentIcon(hoveredField.category) as
                  React.ComponentType<{ size?: number; className?: string }> | undefined
                return IconComponent ? (
                  <IconComponent size={18} className='text-zinc-500 mb-2' />
                ) : null
              })()}
            </div>
            {hoveredEventName ? (
              <div>
                <p className='text-text-sm-semibold text-zinc-800'>
                  {mapEventData(hoveredEventName, hoveredField.metadata).name}
                </p>
                <p className='text-text-xs-regular text-zinc-500 leading-relaxed'>
                  {mapEventData(hoveredEventName, hoveredField.metadata).description}
                </p>
              </div>
            ) : (
              <div>
                <p className='text-text-sm-semibold text-zinc-800'>
                  {hoveredField.name}
                </p>
                {hoveredField.description && (
                  <p className='text-text-xs-regular text-zinc-500 leading-relaxed'>
                    {hoveredField.description}
                  </p>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className='flex items-center justify-center h-full'>
            <p className='text-text-sm-regular text-zinc-400 text-center'>
              Hover over a filter to see details
            </p>
          </div>
        )}
      </div>
    </>
  )
}

const FilterModal: React.FC<FilterModalProps> = ({
  trigger,
  onSelectField
}) => {
  const { t } = useLocale()
  const { schemaData, isLoadingSchemaData } = useDynamicSegment()

  const [searchTerm, setSearchTerm] = useState('')
  const [activeTab, setActiveTab] = useState<CategoryTabType>('ALL')
  const [isOpen, setIsOpen] = useState(false)

  const filteredFields = useMemo(() => {
    let fields = schemaData.fields

    fields = fields.filter(field => field.columnType !== 'AGGREGATED')

    if (searchTerm.trim()) {
      const lowerSearch = searchTerm.toLowerCase()
      fields = fields.filter(field =>
        field.name.toLowerCase().includes(lowerSearch) ||
        field.description?.toLowerCase().includes(lowerSearch)
      )
    }

    if (activeTab !== 'ALL') {
      if (activeTab === 'OTHERS') {
        const primaryCategoryKeys = DynamicSegmentColumnCategoryTabs.filter(tab => tab.key !== 'OTHERS').map(tab => tab.key)
        fields = fields.filter(
          field => !primaryCategoryKeys.includes(field.category as CategoryTabType)
        )
      } else {
        fields = fields.filter(field => field.category === activeTab)
      }
    }

    return fields
  }, [schemaData.fields, searchTerm, activeTab])

  const groupedFields = useMemo(() => {
    const groups: Record<string, DynamicSegmentFieldType[]> = {}

    filteredFields.forEach(field => {
      let groupKey: string
      const categories = DynamicSegmentColumnCategoryTabs.map(tab => tab.key)
      if (categories.includes(field.category as CategoryTabType)) {
        groupKey = field.category as CategoryTabType
      } else {
        groupKey = 'OTHERS'
      }

      if (!groups[groupKey]) {
        groups[groupKey] = []
      }
      groups[groupKey].push(field)
    })
    return groups
  }, [filteredFields])

  const defaultHover = useMemo((): { field: DynamicSegmentFieldType; eventName: string | null } | null => {
    if (!isOpen || isLoadingSchemaData || Object.keys(groupedFields).length === 0) return null
    for (const category of DynamicSegmentColumnCategoryTabs) {
      const fields = groupedFields[category.key]
      if (!fields?.length) continue
      const firstField = fields[0]
      if (category.key === 'EVENT_ATTRIBUTES' && firstField.columnReference === 'EVENT_NAME' && firstField.allowedValues?.length) {
        return { field: firstField, eventName: firstField.allowedValues[0] }
      }
      return { field: firstField, eventName: null }
    }
    return null
  }, [isOpen, isLoadingSchemaData, groupedFields])

  const defaultHoverKey = defaultHover ? `${defaultHover.field.id}:${defaultHover.eventName ?? ''}` : ''

  const handleFieldChosen = ({ field, value }: { field: DynamicSegmentFieldType; value?: string[] }) => {
    const uiFilterType = getDynamicSegmentUIFilterType(field)
    let selected: DynamicSegmentFilterType | undefined

    switch (uiFilterType) {
      case 'SINGLE':
        selected = { field, operator: field.operators[0], values: [], id: generateUUID(), children: [] }
        break
      case 'MULTI': {
        const countField = schemaData.fields.find((f: DynamicSegmentFieldType) => f.tableId === field.tableId && f.columnType === 'AGGREGATED' && f.id === (field.tableId + '_SYS_COUNT'))
        if (!countField) {
          break
        }
        const childFilters: DynamicSegmentFilterType[] = [{ field, operator: field.operators[0], values: [], id: generateUUID(), children: [] }]
        const timesDefault =
          isEventMetricCountField(countField) &&
          countField.operators[0]?.dataType === 'NUMBER' &&
          countField.operators[0]?.valueCount === 1
            ? ['1']
            : []
        selected = {
          field: countField,
          operator: countField.operators[0],
          values: timesDefault,
          id: generateUUID(),
          children: childFilters
        }
        break
      }
      case 'EVENT': {
        const countField = schemaData.fields.find((f: DynamicSegmentFieldType) => f.tableId === field.tableId && f.columnType === 'AGGREGATED' && f.id === (field.tableId + '_SYS_COUNT'))
        const timeStampField = schemaData.fields.find((f: DynamicSegmentFieldType) => f.tableId === field.tableId && f.columnType === 'RAW' && f.columnReference === 'SERVER_TIMESTAMP')
        if (!countField || !timeStampField) {
          break
        }
        const childFilters: DynamicSegmentFilterType[] = [{ field, operator: field.operators[0], values: value || [], id: generateUUID(), children: [] }, { field: timeStampField, operator: timeStampField.operators[0], values: [], id: generateUUID(), children: [] }]
        const timesDefaultEvent =
          isEventMetricCountField(countField) &&
          countField.operators[0]?.dataType === 'NUMBER' &&
          countField.operators[0]?.valueCount === 1
            ? ['1']
            : []
        selected = {
          field: countField,
          operator: countField.operators[0],
          values: timesDefaultEvent,
          id: generateUUID(),
          children: childFilters
        }
        break
      }
    }

    setIsOpen(false)
    setSearchTerm('')
    setActiveTab('ALL')

    if (selected) {
      onSelectField(selected)
    }
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open) {
      setSearchTerm('')
      setActiveTab('ALL')
    }
  }

  const hasListContent = !isLoadingSchemaData && Object.keys(groupedFields).length > 0

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        {trigger}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className='w-[532px] p-0 bg-white rounded-xl overflow-hidden'
        side='bottom'
        align='end'
        sideOffset={4}
        avoidCollisions={true}
        collisionPadding={20}
      >
        <div className='px-1.5 py-0.5 bg-zinc-50 border-b border-zinc-950/8'>
          <Input
            type='text'
            variant='filled'
            dimension='md'
            placeholder='Search...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
            startAdornment={<MagnifyingGlass size={18} className='text-zinc-400' />}
            className='!bg-transparent !shadow-none focus-within:!shadow-none focus-within:!bg-transparent'
            autoFocus
          />
        </div>

        <div className='px-2 py-2'>
          <Tabs value={activeTab} onValueChange={key => setActiveTab(key as CategoryTabType)}>
            <TabsList variant='neutral' size='sm'>
              <TabsTrigger value='ALL'>{t('segment_creation.all')}</TabsTrigger>
              {DynamicSegmentColumnCategoryTabs.map((tab: CategoryTabConfigType) => (
                <TabsTrigger
                  key={tab.key}
                  value={tab.key}
                  leftIcon={getDynamicSegmentIcon(tab.key)}
                  iconProps={{ leftIconProps: { size: 'xs', className: 'text-zinc-500' } }}
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        <div className='h-[12px] border-t-2 border-dotted border-zinc-950/8' />

        <div className='flex min-h-[300px] max-h-[400px]'>
          {isLoadingSchemaData ? (
            <>
              <div className='w-[257px] border-r border-zinc-100 overflow-y-auto'>
                <div className='flex flex-col gap-2 p-4 animate-pulse'>
                  {[1, 2, 3, 4, 5].map(category => (
                    <div key={category} className='flex flex-col gap-2'>
                      <div className='h-12 w-24 bg-zinc-200 rounded' />
                      {[1, 2, 3].map(item => (
                        <div key={item} className='h-8 w-full bg-zinc-200 rounded' />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
              <div className='flex-1 p-5 bg-white overflow-y-auto'>
                <div className='flex flex-col gap-2 animate-pulse'>
                  <div className='h-6 w-32 bg-zinc-200 rounded' />
                  <div className='h-4 w-full bg-zinc-200 rounded' />
                  <div className='h-4 w-3/4 bg-zinc-200 rounded' />
                  <div className='h-4 w-2/3 bg-zinc-200 rounded mt-4' />
                </div>
              </div>
            </>
          ) : !hasListContent ? (
            <>
              <div className='w-[257px] border-r border-zinc-100 overflow-y-auto'>
                <div className='flex items-center justify-center h-full p-4'>
                  <p className='text-text-sm-regular text-zinc-400'>{t('segment_creation.no_filters_found')}</p>
                </div>
              </div>
              <div className='flex-1 p-5 bg-white overflow-y-auto'>
                <div className='flex items-center justify-center h-full'>
                  <p className='text-text-sm-regular text-zinc-400 text-center'>
                    Hover over a filter to see details
                  </p>
                </div>
              </div>
            </>
          ) : (
            <FilterModalFieldPanel
              key={defaultHoverKey}
              groupedFields={groupedFields}
              defaultHover={defaultHover}
              t={t}
              onFieldChosen={handleFieldChosen}
            />
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default FilterModal
